import { useState } from 'react';

const PORTAL_ID = '244482894';
const FORM_ID   = '1547d8f4-a252-41e0-97dc-590b9c0705d8';

function readHubspotutk() {
  if (typeof document === 'undefined') return '';
  const match = document.cookie.match(/(?:^|;\s*)hubspotutk=([^;]+)/);
  return match ? match[1] : '';
}

const FIELDS = [
  { name: 'firstname', label: 'First Name', type: 'text',     required: true,  row: 'a' },
  { name: 'lastname',  label: 'Last Name',  type: 'text',     required: true,  row: 'a' },
  { name: 'email',     label: 'Email',      type: 'email',    required: true,  row: 'b' },
  { name: 'jobtitle',  label: 'Job Title',  type: 'text',     required: false, row: 'c' },
  { name: 'company',   label: 'Company',    type: 'text',     required: false, row: 'd' },
  { name: 'message',   label: 'Message',    type: 'textarea', required: false, row: 'e', rows: 4 },
];

export default function HubSpotForm() {
  const initial = Object.fromEntries(FIELDS.map(f => [f.name, '']));
  const [values, setValues]   = useState(initial);
  const [status, setStatus]   = useState('idle');   // idle | submitting | success | error
  const [errorMsg, setErrorMsg] = useState('');

  const onChange = name => e =>
    setValues(v => ({ ...v, [name]: e.target.value }));

  const onSubmit = async e => {
    e.preventDefault();
    setStatus('submitting');
    setErrorMsg('');

    const hutk    = readHubspotutk();
    const context = {
      pageUri:  typeof window   !== 'undefined' ? window.location.href  : '',
      pageName: typeof document !== 'undefined' ? document.title        : '',
    };
    if (hutk) context.hutk = hutk;

    const payload = {
      fields: FIELDS.map(f => ({
        objectTypeId: '0-1',
        name:  f.name,
        value: values[f.name] || '',
      })),
      context,
    };

    try {
      const res = await fetch(
        `https://api.hsforms.com/submissions/v3/integration/submit/${PORTAL_ID}/${FORM_ID}`,
        {
          method:  'POST',
          headers: { 'Content-Type': 'application/json' },
          body:    JSON.stringify(payload),
        }
      );

      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        setErrorMsg(
          body?.errors?.[0]?.message ||
          body?.message ||
          `Submission failed (${res.status})`
        );
        setStatus('error');
        return;
      }

      setStatus('success');
    } catch {
      setErrorMsg('Network error — please try again.');
      setStatus('error');
    }
  };

  if (status === 'success') {
    return (
      <div className="fd-form-success" role="status">
        Thanks — you're on the list. We'll be in touch shortly.
      </div>
    );
  }

  // Group fields into rows: row 'a' renders as two columns
  const rowGroups = FIELDS.reduce((acc, f) => {
    if (!acc[f.row]) acc[f.row] = [];
    acc[f.row].push(f);
    return acc;
  }, {});

  return (
    <form className="fd-form" onSubmit={onSubmit} noValidate>
      {Object.entries(rowGroups).map(([row, rowFields]) => (
        <div
          key={row}
          className={rowFields.length > 1 ? 'fd-form-row fd-form-row--2col' : 'fd-form-row'}
        >
          {rowFields.map(f => {
            const id = `fd-${f.name}`;
            const common = {
              id,
              name:         f.name,
              value:        values[f.name],
              onChange:     onChange(f.name),
              required:     !!f.required,
              placeholder:  f.placeholder || '',
            };
            return (
              <div className="fd-form-field" key={f.name}>
                <label htmlFor={id}>
                  {f.label}
                  {f.required && <span className="fd-form-req" aria-hidden="true"> *</span>}
                </label>
                {f.type === 'textarea' ? (
                  <textarea rows={f.rows || 4} {...common} />
                ) : (
                  <input type={f.type || 'text'} {...common} />
                )}
              </div>
            );
          })}
        </div>
      ))}

      {status === 'error' && (
        <div className="fd-form-error" role="alert">{errorMsg}</div>
      )}

      <button
        type="submit"
        className="fd-form-submit"
        disabled={status === 'submitting'}
      >
        {status === 'submitting' ? 'Sending…' : 'Request Early Access'}
      </button>
    </form>
  );
}
